
<script setup lang="ts">

import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

const isSessionExpired = computed(() => route.query.reason === 'session_expired')
const isSessionChanged = computed(() => route.query.reason === 'session_changed')

function goToCorp() {
  router.push({
    path: '/corp/login',
    query: {
      redirect: route.query.redirect || '/'
    }
  })
}

function goToDeveloper() {
  router.push({
    path: '/developer/login',
    query: {
      redirect: route.query.redirect || '/'
    }
  })
}

function goToConsole() {
  router.push({
    path: '/console/login',
    query: {
      redirect: route.query.redirect || '/'
    }
  })
}

</script>

<template>
  <div class="landing-wrapper">
    <div v-if="isSessionExpired" class="session-banner session-banner--expired">
      Your session has expired. Please log in again to continue.
    </div>
    <div v-if="isSessionChanged" class="session-banner session-banner--changed">
      A different session type was detected. You have been logged out for security. Please log in again.
    </div>
    <div class="landing-card">
      <div class="title-login">Q2 Mobile Distribution Site</div>
      <div class="description">
        A centralized platform to distribute mobile apps and provide a seamless experience for downloading builds.
      </div>
      <div class="login-options">
        <button @click="goToConsole" class="console-btn">
          <div class="icon-wrapper">
            <img src="@/assets/q2_console_clear_bg.png" alt="Q2 Console" class="btn-icon" />
          </div>
          SIGN IN WITH Q2 CONSOLE LOGIN
        </button>
        <button @click="goToDeveloper" class="developer-btn">
          <div class="icon-wrapper">
            <img src="@/assets/q2_developer_clear_bg.svg" alt="Q2 Developer" class="btn-icon" />
          </div>
          SIGN IN WITH Q2 DEVELOPER
        </button>
        <button @click="goToCorp" class="okta-btn">
          <div class="icon-wrapper">
            <img src="@/assets/okta_generic.svg" alt="Okta" class="btn-icon btn-icon--okta" />
          </div>
          <span class="okta-text">SIGN IN WITH Q2 OKTA</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.landing-wrapper {
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  box-sizing: border-box;
  text-align: center;
  background: linear-gradient(to bottom right, #f0f4f8, #d9e2ec);
}

.landing-card {
  background: white;
  border-radius: 12px;
  padding: 3rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.intro {
  font-weight: 500;
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.5rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.title-login {
  font-weight: 800;
  color: #1a202c;
  font-size: 2.25rem;
  line-height: 1.2;
  margin-bottom: 1rem;
}

.description {
  color: #4a5568;
  font-size: 1rem;
  line-height: 1.6;
  max-width: 400px;
  margin-bottom: 2rem;
}

.login-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 500px;
}


.console-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 3.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  background: #00796b;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  justify-content: flex-start;
}

.console-btn:hover {
  background: #00574b;
}

.okta-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 3.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid #00796b;
  border-radius: 6px;
  background: white;
  color: #00796b;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.okta-btn:hover {
  background: #e0f2f1;
}

.btn-icon {
  min-width: 80px;
  max-width: 120px;
  height: auto;
  object-fit: contain;
}

.icon-wrapper {
  width: 120px;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon--okta {
  min-width: 60px;
  max-width: 80px;
}

.okta-text {
  margin-left: 10px;
}

.developer-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 3.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid #00796b;
  border-radius: 6px;
  background: white;
  color: #00796b;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  justify-content: flex-start;
}

.developer-btn:hover {
  background: #e0f2f1;
}

.session-banner {
  padding: 12px 24px;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  max-width: 520px;
  width: 100%;
  font-size: 0.95rem;
  text-align: center;
  box-sizing: border-box;
}

.session-banner--expired {
  background-color: #FFF3E0;
  border: 1px solid #FF9800;
  color: #E65100;
}

.session-banner--changed {
  background-color: #FFF3E0;
  border: 1px solid #FF9800;
  color: #E65100;
}
</style>
